/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useMemo, useState } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { Row, Col, Switch, Select, Button } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";

import "../../Styles/styles-TeamData.css";
import "../../Styles/styles-PostData.css";
import { useSelector } from "react-redux";
import {
	setTeamFilter,
	setTeamPresets,
	setTestingStatDefaults,
} from "../../store/actions/team.actions";
import { setPlayerFilter } from "../../store/actions/player.actions";
import { getDropdownKey } from "../../Utils/chart_utils";
import { useDispatch } from "react-redux";
import { LoadingComponent } from "../Common/LoadingComponent";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx"
import { StatTileV2 } from "../StatTileV2/StatTileV2";
import { StatChartV2 } from "../StatChartV2/StatChartV2";
import OptionalFeaturesForCharts from "../Charts/OptionalFeaturesForCharts.jsx";
import { ForceplateAndTestingBreakdownTable } from "../Team/ForceplateAndTestingBreakdownTable.jsx";
import { TestingForceplateMetricTable } from "../Team/TestingForceplateMetricTable.jsx";
import { RadarCharts } from "./RadarCharts.jsx";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { useDataAccess, DataAccessDenied } from "@components/PermissionedComponents";
import { get_metric_info_from_identifier } from "../../Utils/chart_utils";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";

let trainingAPI = new TrainingAPI();

function TestingData({ type, handleComparisonOptionChange, ...props }) {
	const dispatch = useDispatch();
	const PRESET_TYPE = PresetType.Testing;
	const DEFAULT_PRESET = 'Default'

	const {
		all_metric_types,
		testingStatDefaults,
		misc: { comparison_types },
		info: { team_colors: teamColors },
		filter: {player_group, session},
		dropdowns: {testing: testingDropdowns },
		presets
	} = useSelector(({ team }) => team);

	const {
		loading: { testing: testingLoading },
		filter: { testingStats, endDate, player, startDate, comparisonOption },
	} = useSelector((store) => store[type]);

	const {
		user,
	  } = useAuth0();

	const [statMetrics, setStatMetrics] = useState([]);
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
	const [radarChartData, setRadarChartData] = useState(testingStatDefaults)
	const [unsavedChanges, setUnsavedChanges] = useState(false)

	useEffect(() => {
		if (currentSelectedTilePreset === DEFAULT_PRESET) {
			setRadarChartData(testingStatDefaults)
		} else {
			const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
			setRadarChartData(presetModified?.config ?? [])
		}
	}, [currentSelectedTilePreset])

	const { isDenied: isDataDenied } = useDataAccess();

	useMetricDropdowns(['testing']);

	if (type !== "team" && type !== "player") return null;

	// const onChangeStats = () => {
	// 	if (type === "team") {
	// 		dispatch(setTeamFilter({ testingStats: !testingStats }));
	// 	} else if (type === "player") {
	// 		dispatch(setPlayerFilter({ testingStats: !testingStats }));
	// 	}
	// };

	const handleSaveMetrics = async () => {
		const filtered = statMetrics.filter((val) => !!val);

		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(filtered, "testing");
				dispatch(setTestingStatDefaults(filtered));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: filtered, preset_id: presetModified.preset_id
				});
				presetModified.config = filtered
				dispatch(setTeamPresets(presets))
			}
			setRadarChartData(filtered)
			message.success("Saved!");
		} catch (e) {
			console.log(e);
			message.error("Error. Unable to edit ");
		}
	};

	const handleChangeMetric = (index) => (key) => {
		setStatMetrics((prevStatMetrics) => {
			const newStatMetrics = [...prevStatMetrics];
			newStatMetrics[index] = key;
			return newStatMetrics;
		});

		setUnsavedChanges(true)
	};

	if (isDataDenied("testing")) {
	  return <DataAccessDenied streams={["testing"]} variant="inline" />;
	}

	return (
		<LoadingComponent loading={testingLoading}>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					className="flex flex-col justify-center items-center w-full"
					id="statistics-widget"
				>
					<Row
						gutter={16}
						style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
						<PresetSelection 
							presets={presets} 
							user={user} 
							presetType={PRESET_TYPE} 
							statDefaults={testingStatDefaults} 
							currStatMetrics={statMetrics}
							setStatMetrics={setStatMetrics} 
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={comparisonOption}
							setComparisonOption={handleComparisonOptionChange}
							handleSave={handleSaveMetrics}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
							/>
					</Row>
					
					<Row
						gutter={16}
						style={{ width: "90%", marginBottom: 16}}
						className="exportable tab-testing testing-stats"
					>
						{statMetrics
							.concat(new Array(4).fill(undefined))
							.slice(0, 4)
							.map((metric, i) => {
								return (
									<Col
										span={6}
										key={i}
										className={`${props.metric_type}-stat-tile`}
									>
								
										<div
										key={i}
										style={{
											width: "100%",
											height: "100%", 
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
										}}
										>
										
											<CompactStatTile
												dropdowns={testingDropdowns}
												metric_identifier_with_stat={metric}
												onChange={handleChangeMetric(i)}
												option={comparisonOption}
												endDate={endDate}
												startDate={startDate}
												isCompact={true}
												type={type}
												comparison={comparisonOption}
												{...(type === "player" && {
													playerId: player,
												})}
												positionFilter={
													props.position_filter
												}
												/>

									</div>

									</Col>
								);
							})}
					</Row>
				</div>

				<TestingForceplateMetricTable
					metricType="testing"
					metricIdentifiers={statMetrics}
					positionFilter={props.position_filter}
					{...(type === "player" && { playerId: player })}
				/>

				<RadarCharts
					type={type}
					metric_type={'testing'}
					{...(type === "player" && { playerId: player})}
					chartData={radarChartData}
				/>

				<Row gutter={16} style={{ width: "80%" }}>
					{statMetrics
						.concat([undefined, undefined, undefined, undefined])
						.slice(0, 4)
						.map((metric, i) => {
							let {metric_id, metric_stat} = get_metric_info_from_identifier(metric);
							return (
                            <Col lg={24} xl={12} key={i}>
                                <OptionalFeaturesForCharts
                                    chartType={'stat'}
									showAvgPlotline={true}
                                    optionalFeatures={{showTrendlines: "Trendline" }}
                                    value={metric_id}
                                    metricType="testing"
                                    startDate={startDate}
                                    endDate={endDate}
                                    {...(type === "player" && {
                                        playerId: player,
                                    })}
                                    stat={metric_stat || 'avg'}
                                    type={type}
                                    position={props.position_filter}
                                    colors={teamColors.all}
                                    player_group={player_group}
                                    sessions={session}
                                    enableDrillDown={true}
									dataColorUtil={props.dataColorUtil}
                                />
                            </Col>
						)})}
				</Row>
				{type === "team" && (
					<ForceplateAndTestingBreakdownTable
						showAll={props.showAll}
						type={type}
						metric_type="testing"
						start_date={startDate}
						end_date={endDate}
						position_filter={props.position_filter}
						stat={"Avg"}
					/>
				)}
			</div>
		</LoadingComponent>
	);
}

export default TestingData;